import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import HubCard from './components/HubCard';
import { AdherenceCard } from './components/AdherenceCard';
import { VitalsCard } from './components/VitalsCard';
import { PatientSurveyCard } from './components/PatientSurveyCard';
import { WellnessCard } from './components/WellnessCard';
import { DashboardTabs } from './components/DashboardTabs';
import { SoftwareCard } from './components/SoftwareCard';
import { IssuesCard } from './components/IssuesCard';
import DetailPanel from './detailPanel/DetailPanel';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchFacilityOverviewStats,
  fetchOverviewStats,
} from '../../store/overviewStats/actionCreators';
import { RootState } from '../../store';
import { DailyWellnessStats } from '../../store/overviewStats/overviewStatsTypes';
import LoadingSpinner from '../Shared/LoadingSpinner';
import AddNewController from './AddNewController/AddNewController';
import { getFacilityRoleById } from '../../shared/utils';
import { UserRoles } from '../../shared/constants';
import { FloatingChatbot } from '../Chatbot/FloatingChatbot';

// This is used as fallback when API data is not available
const EMPTY_STATS = {
  adherence: { adherent: 0, non_adherent: 0, unknown: 0 },
  hubs: { offline: 0, unassigned: 0, online: 0, total: 0 },
  issues: { total: 0, high_priority: 0, low_priority: 0 },
  patient_survey: {
    very_happy: 0,
    somewhat_happy: 0,
    somewhat_unhappy: 0,
    very_unhappy: 0,
  },
  daily_wellness: { very_bad: 0, bad: 0, neutral: 0, good: 0, very_good: 0 },
  software: { up_to_date: 0, need_updating: 0 },
};

// daily_wellness is missing on backends older than OI-1772; default every
// bucket so the Wellness card renders its "No responses" state instead of crashing.
const normalizeWellness = (
  raw?: Partial<DailyWellnessStats> | null,
): DailyWellnessStats => ({
  very_bad: raw?.very_bad ?? 0,
  bad: raw?.bad ?? 0,
  neutral: raw?.neutral ?? 0,
  good: raw?.good ?? 0,
  very_good: raw?.very_good ?? 0,
});

const CustomerCareDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const { stats, isLoading } = useSelector(
    (state: RootState) => state.overviewStats,
  );

  const { dashboardUserInfo } = useSelector(
    (state: RootState) => state.dashboardUser,
  );

  const currentRole = dashboardUserInfo?.facility_role_id
    ? getFacilityRoleById(dashboardUserInfo.facility_role_id)
    : null;

  const isFacilityAdmin = currentRole === UserRoles.FACILITY_ADMIN;

  useEffect(() => {
    if (!stats) {
      if (isFacilityAdmin && dashboardUserInfo?.facility_id) {
        dispatch(fetchFacilityOverviewStats(dashboardUserInfo.facility_id));
      } else {
        dispatch(fetchOverviewStats());
      }
    }
  }, [dispatch, stats, isFacilityAdmin, dashboardUserInfo]);

  const normalizeStats = (stats: any, selectedTab: number) => {
    if (!stats) return EMPTY_STATS;
    // Old CC response
    if ('all' in stats) {
      const category =
        selectedTab === 1
          ? stats.facilities
          : selectedTab === 2
            ? stats.consumers
            : stats.all;
      return {
        ...category,
        daily_wellness: normalizeWellness(category?.daily_wellness),
      };
    }
    // Facility admin response
    // Normalize software keys from facility overview API
    const softwareRaw = stats.software || {};
    const upToDate = softwareRaw.up_to_date ?? softwareRaw['Up-to-date'] ?? 0;
    const needUpdating =
      softwareRaw.need_updating ?? softwareRaw['Need updating'] ?? 0;
    const unknown = softwareRaw.unknown ?? softwareRaw['Unknown'] ?? 0;

    return {
      adherence: {
        adherent: stats.adherence.adherent ?? 0,
        non_adherent:
          stats.adherence.nonAdherent ?? stats.adherence.non_adherent ?? 0,
        unknown: stats.adherence.unknown ?? 0,
      },
      hubs: {
        offline: stats.hubs.offline ?? 0,
        unassigned: stats.hubs.unassigned ?? 0,
        online: stats.hubs.online ?? 0,
        total: stats.hubs.total ?? 0,
      },
      issues: {
        total:
          (stats.issues?.high_priority ?? 0) +
          (stats.issues?.low_priority ?? 0),
        high_priority: stats.issues?.high_priority ?? 0,
        low_priority: stats.issues?.low_priority ?? 0,
      },
      patient_survey: {
        very_happy: stats.patient_survey?.very_happy ?? 0,
        somewhat_happy: stats.patient_survey?.somewhat_happy ?? 0,
        somewhat_unhappy: stats.patient_survey?.somewhat_unhappy ?? 0,
        very_unhappy: stats.patient_survey?.very_unhappy ?? 0,
      },
      daily_wellness: normalizeWellness(stats.daily_wellness),
      software: {
        up_to_date: upToDate,
        need_updating: needUpdating,
        unknown: unknown,
      },
    };
  };
  const safeStats = normalizeStats(stats, selectedTab);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <DashboardTabs
        selectedTab={selectedTab}
        onTabChange={(tab) => setSelectedTab(tab)}
        buttonConfig={{
          show: true,
          label: 'Add new',
          onClick: () => setIsAddModalOpen(true),
        }}
        isFacilityAdmin={isFacilityAdmin}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(auto-fill, minmax(300px, 1fr))',
            sm: 'repeat(auto-fill, minmax(300px, 1fr))',
            md: 'repeat(auto-fill, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(3, 1fr)',
          },
          gap: 3,
        }}
        px={2}
        pt={2}
      >
        <AdherenceCard
          isSelected={selectedCard === 'adherence'}
          onClick={() => setSelectedCard('adherence')}
          data={{
            adherent: safeStats.adherence.adherent,
            nonAdherent: safeStats.adherence.non_adherent,
            unknown: safeStats.adherence.unknown,
          }}
        />
        <HubCard
          isSelected={selectedCard === 'hubs'}
          onClick={() => setSelectedCard('hubs')}
          data={{
            offline: safeStats.hubs.offline,
            unassigned: safeStats.hubs.unassigned,
            online: safeStats.hubs.online,
            total: safeStats.hubs.total,
          }}
        />
        <IssuesCard
          isSelected={selectedCard === 'issues'}
          onClick={() => setSelectedCard('issues')}
          data={{
            total: safeStats.issues.total,
            highPriority: safeStats.issues.high_priority,
            lowPriority: safeStats.issues.low_priority,
          }}
        />
        <PatientSurveyCard
          isSelected={selectedCard === 'survey'}
          onClick={() => setSelectedCard('survey')}
          data={{
            veryHappy: safeStats.patient_survey.very_happy,
            somewhatHappy: safeStats.patient_survey.somewhat_happy,
            somewhatUnhappy: safeStats.patient_survey.somewhat_unhappy,
            veryUnhappy: safeStats.patient_survey.very_unhappy,
          }}
        />
        <WellnessCard
          isSelected={selectedCard === 'wellness'}
          onClick={() => setSelectedCard('wellness')}
          data={{
            veryBad: safeStats.daily_wellness.very_bad,
            bad: safeStats.daily_wellness.bad,
            neutral: safeStats.daily_wellness.neutral,
            good: safeStats.daily_wellness.good,
            veryGood: safeStats.daily_wellness.very_good,
          }}
        />
        <SoftwareCard
          isSelected={selectedCard === 'software'}
          onClick={() => setSelectedCard('software')}
          data={{
            version: stats?.stable_channel_software_version || 'N/A',
            upToDate: safeStats.software.up_to_date,
            needsUpdate: safeStats.software.need_updating,
          }}
        />
        <VitalsCard
          isSelected={selectedCard === 'vitals'}
          onClick={() => setSelectedCard('vitals')}
          data={{
            labels: ['BP', 'HR', 'SpO2', 'RR'],
            normal: [80, 70, 95, 12],
            atRisk: [10, 20, 5, 8],
            actionRequired: [10, 10, 0, 2],
          }}
          comingSoon
        />
      </Box>

      {<DetailPanel card={selectedCard || ''} selectedTab={selectedTab} />}

      {isAddModalOpen && (
        <AddNewController
          onClose={() => setIsAddModalOpen(false)}
          isFacilityAdmin={isFacilityAdmin}
        />
      )}

      <FloatingChatbot />
    </Box>
  );
};

export default CustomerCareDashboard;
